import Emitter from "@homer.core/emitter";
import application from "@homer.core/application";
import Uuid from "@homer.utility/uuid";

const lookup = new Map();
const uuid = Uuid.generate();

export default class Channel extends Emitter {
    #name;
    #channel = null;
    #onMessage = (event) => {
        if (!event.isTrusted || event.origin !== location.origin) {
            return;
        }

        const { source, payload } = event.data;

        if (application.environment !== "production") {
            console.info(`[${this.#name}][broadcast:received]`, { source, target: this.uuid }, payload);
        }

        this.emit("broadcast", payload, source !== this.uuid);
    };

    constructor(name) {
        super();
        this.#name = name;

        Object.defineProperty(this, "uuid", {
            get: () => uuid,
            enumerable: true,
        });

        // BFCache/WebView restores can reuse the same Channel instance after pagehide, so lazily reopen the transport.
        this.#open();

        globalThis.addEventListener("pagehide", () => {
            this.#close();
        });

        globalThis.addEventListener("pageshow", (event) => {
            if (event.persisted) {
                this.#open();
            }
        });
    }

    #open() {
        if (this.#channel) {
            return this.#channel;
        }

        this.#channel = new BroadcastChannel(this.#name);
        this.#channel.addEventListener("message", this.#onMessage);

        return this.#channel;
    }

    #close(channel = this.#channel) {
        if (!channel) {
            return;
        }

        channel.removeEventListener("message", this.#onMessage);
        channel.close();

        if (this.#channel === channel) {
            this.#channel = null;
        }
    }

    broadcast(payload) {
        const message = { source: this.uuid, payload };
        let channel = this.#open();

        if (application.environment !== "production") {
            console.info(`[${this.#name}][broadcast:sent]`, { source: this.uuid }, payload);
        }

        try {
            channel.postMessage(message);
        } catch (error) {
            // BFCache/WebView lifecycle can close the channel between open and send, so reopen once on the stale transport.
            if (error.name !== "InvalidStateError") {
                throw error;
            }

            this.#close(channel);
            channel = this.#open();
            channel.postMessage(message);
        }
    }

    static obtain(name) {
        return lookup.get(name) || lookup.set(name, new Channel(name)).get(name);
    }
}
